import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import TypographyMui from '@material-ui/core/Typography';
import { Tooltip, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { Typography, TypographyProps } from '../Typography/Typography';

interface Props extends TypographyProps {
  maxWidth?: number;
}

/**
 * Typography component that displays a tooltip when the specified max width is reached.
 * If no max width is specified the parent component must have a width. Child component
 * needs to be text. As is built with the UDS Typography component any Typography
 * component properties can be used.
 *
 */
export const TypographyTooltip: FC<React.PropsWithChildren<Props>> = ({
  maxWidth,
  className,
  children,
  style,
  ...props
}) => {
  const [showTooltip, setTooltipState] = useState(false);
  const typographyRef = useRef<HTMLDivElement | null>(null);

  return (
    <StyledTypographyMui
      className={className}
      $maxWidth={maxWidth}
      ref={typographyRef}
      style={style}
      noWrap
      onMouseEnter={() => setTooltipState(shouldEnableTooltip(typographyRef.current))}
    >
      <Tooltip title={<StyledTypography>{children}</StyledTypography>} disabled={!showTooltip} placement="top">
        <StyledTypography tag="span" {...props}>
          {children}
        </StyledTypography>
      </Tooltip>
    </StyledTypographyMui>
  );
};

const StyledTypographyMui = styled(TypographyMui)<{ $maxWidth?: number }>`
  ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth}px` : '')};

  & {
    color: ${({ theme }) => theme.colorOnSurface}!important;

    a {
      // Remove default anchor styles for tooltip.
      color: ${({ theme }) => theme.colorOnSurface}!important;
      text-decoration: none;
    }
  }
`;

const StyledTypography = styled(Typography)`
  // Allow UDS Typography component to be used and for styles to cascade.
  &,
  div {
    display: inline;
    overflow: inherit;
    white-space: inherit;
    text-overflow: inherit;
  }
`;
